import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useEvent } from '../hooks/useEvents'
import { useRoster } from '../hooks/useRoster'
import { useMe } from '../hooks/useMe'
import { useUsers } from '../hooks/useUsers'
import {
  useEventAvailability,
  useMyAvailability,
  useUpsertAvailability
} from '../hooks/useAvailability'
import { Icon } from '../components/Icon'
import { parseIsoDate, formatDateRange } from '../lib/dates'
import type {
  LibraryRole, RosterAvailability, RosterAvailabilityEntry
} from '../types'

// EventAvailability — per-event marker page (mig 041, PR R3).
//
// Self-service: any signed-in user marks their availability for the
// event's active roles × days. Admin override: "Mark on behalf of"
// user picker switches the target to another user, and admins see /
// edit anyone's grid.
//
// Bulk affordances:
//   - Click a column header → toggle every cell in that column
//   - Click a row header    → toggle every cell in that row
//   Toggle semantics: if any cell is unticked, tick all; else untick all.
//   Drag-rectangle selection is in the out-of-scope list (v2).
//
// Save diffs the local set against the server snapshot and POSTs only
// the changed entries — keeps the batch tight.

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function EventAvailability() {
  const { id } = useParams()
  const eventId = Number(id)
  const { data: me } = useMe()
  const { data: event } = useEvent(eventId)
  const { data: roster } = useRoster(eventId)
  const { data: users } = useUsers()
  const { data: myRows } = useMyAvailability(eventId)
  const { data: allRows } = useEventAvailability(eventId, !!me?.isAdmin)
  const upsertMut = useUpsertAvailability(eventId)

  // Target user — defaults to the caller. Admins can switch.
  const [targetUserId, setTargetUserId] = useState<number | null>(null)
  useEffect(() => {
    if (targetUserId === null && me?.dbUserId != null) {
      setTargetUserId(me.dbUserId)
    }
  }, [me?.dbUserId, targetUserId])

  // The active row source: admin viewing self = myRows;
  // admin viewing someone else = allRows filtered;
  // non-admin = myRows. Server enforces auth, we just pick the
  // cleanest cache shape.
  const rowsForTarget: RosterAvailability[] = useMemo(() => {
    if (targetUserId == null) return []
    if (me?.isAdmin && allRows) {
      return allRows.filter((r) => r.userId === targetUserId)
    }
    return myRows ?? []
  }, [me?.isAdmin, allRows, myRows, targetUserId])

  // Picked set: Set<`${roleId}|${date}`>. Local edit state. Seeded
  // from rowsForTarget when targetUserId or the underlying data
  // changes; resets cleanly on user-picker switch.
  const [picked, setPicked] = useState<Set<string>>(new Set())
  // Track which target the picked set was seeded for, so we re-seed
  // when the user-picker changes.
  const [pickedFor, setPickedFor] = useState<number | null>(null)
  // A version counter for the source data so a fresh fetch (after a
  // successful save, say) re-seeds the picked set.
  const sourceKey = useMemo(
    () => `${targetUserId ?? '?'}|${rowsForTarget.map((r) => r.id).join(',')}`,
    [targetUserId, rowsForTarget]
  )
  useEffect(() => {
    if (targetUserId == null) return
    // Re-seed when the target changes OR when the underlying rows
    // change (e.g. after a successful save the upsert response refresh
    // bumps rowsForTarget identity).
    if (pickedFor !== targetUserId) {
      const next = new Set<string>()
      for (const r of rowsForTarget) next.add(`${r.roleId}|${r.availableDate}`)
      setPicked(next)
      setPickedFor(targetUserId)
    }
  }, [sourceKey, targetUserId, pickedFor, rowsForTarget])

  // Bulk note applied to newly-ticked cells. Existing rows keep their
  // own note (we only send a note for inserts, not deletes).
  const [bulkNote, setBulkNote] = useState('')

  if (!event || !roster) {
    return <div className="text-ink-soft text-sm font-mono">Loading availability…</div>
  }

  const days = roster.days
  const roles = roster.roles
  const eventDateRange = formatDateRange(event.startDate, event.endDate)
  const targetUser = users?.find((u) => u.id === targetUserId) ?? null
  const isSelf = targetUserId === me?.dbUserId

  function toggleCell(roleId: number, date: string) {
    const key = `${roleId}|${date}`
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function toggleColumn(date: string) {
    setPicked((prev) => {
      const next = new Set(prev)
      const anyUnticked = roles.some((r) => !next.has(`${r.id}|${date}`))
      if (anyUnticked) {
        for (const r of roles) next.add(`${r.id}|${date}`)
      } else {
        for (const r of roles) next.delete(`${r.id}|${date}`)
      }
      return next
    })
  }

  function toggleRow(roleId: number) {
    setPicked((prev) => {
      const next = new Set(prev)
      const anyUnticked = days.some((d) => !next.has(`${roleId}|${d}`))
      if (anyUnticked) {
        for (const d of days) next.add(`${roleId}|${d}`)
      } else {
        for (const d of days) next.delete(`${roleId}|${d}`)
      }
      return next
    })
  }

  function selectAll() {
    const next = new Set<string>()
    for (const r of roles) for (const d of days) next.add(`${r.id}|${d}`)
    setPicked(next)
  }

  function selectNone() {
    setPicked(new Set())
  }

  function handleSave() {
    if (targetUserId == null) return
    // Diff picked against rowsForTarget. Only send what changed.
    const serverKeys = new Set(
      rowsForTarget.map((r) => `${r.roleId}|${r.availableDate}`)
    )
    const entries: RosterAvailabilityEntry[] = []
    const noteForInserts = bulkNote.trim() === '' ? null : bulkNote.trim()

    // Adds
    for (const key of picked) {
      if (serverKeys.has(key)) continue
      const [rid, date] = key.split('|')
      if (!rid || !date) continue
      entries.push({
        userId: me?.isAdmin && !isSelf ? targetUserId : undefined,
        roleId: Number(rid),
        date,
        available: true,
        note: noteForInserts
      })
    }
    // Removals
    for (const key of serverKeys) {
      if (picked.has(key)) continue
      const [rid, date] = key.split('|')
      if (!rid || !date) continue
      entries.push({
        userId: me?.isAdmin && !isSelf ? targetUserId : undefined,
        roleId: Number(rid),
        date,
        available: false
      })
    }

    if (entries.length === 0) return // nothing to save

    upsertMut.mutate(
      { entries },
      {
        onSuccess: () => {
          setBulkNote('')
        }
      }
    )
  }

  const dirty = useMemo(() => {
    const serverKeys = new Set(
      rowsForTarget.map((r) => `${r.roleId}|${r.availableDate}`)
    )
    if (picked.size !== serverKeys.size) return true
    for (const k of picked) if (!serverKeys.has(k)) return true
    return false
  }, [picked, rowsForTarget])

  const saving = upsertMut.isPending
  const serverError = upsertMut.error as Error | null
  const tickedCount = picked.size

  return (
    <div className="space-y-5">
      <div>
        <Link
          to={`/events/${eventId}`}
          className="text-xs text-ink-soft hover:text-green inline-flex items-center gap-1 mb-1"
        >
          <Icon name="chevronRight" size={11} className="rotate-180" />
          <span>Back to event</span>
        </Link>
        <h1 className="text-3xl mb-0.5">
          {isSelf
            ? 'Mark your availability'
            : `Marking availability for ${targetUser?.name ?? '?'}`}
        </h1>
        <p className="text-ink-soft text-sm">
          <span className="font-semibold">{event.name}</span>
          {' · '}
          <span className="font-mono">{eventDateRange}</span>
        </p>
      </div>

      {me?.isAdmin && users && (
        <div className="bg-white border border-line rounded p-4 flex items-center gap-3 flex-wrap">
          <Icon name="users" size={16} className="text-slate" />
          <span className="text-sm font-semibold">Mark on behalf of</span>
          <select
            value={targetUserId ?? ''}
            onChange={(e) => setTargetUserId(e.target.value === '' ? null : Number(e.target.value))}
            className="border border-line rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-green focus:border-green"
          >
            {me.dbUserId != null && (
              <option value={me.dbUserId}>
                {me.name ?? me.upn ?? 'Me'} (you)
              </option>
            )}
            {users
              .filter((u) => u.id !== me.dbUserId)
              .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
              .map((u) => (
                <option key={u.id} value={u.id}>{u.name ?? '(unnamed)'}</option>
              ))}
          </select>
          {!isSelf && (
            <span className="text-xs text-ink-soft italic">
              Saved changes are audited as on-behalf-of edits.
            </span>
          )}
        </div>
      )}

      {roles.length === 0 ? (
        <div className="bg-white border border-line rounded p-10 text-center">
          <h3 className="font-display text-lg mb-1">No roles configured</h3>
          <p className="text-sm text-ink-soft">
            This event has no roster roles yet. Ask an admin to{' '}
            <Link to={`/events/${eventId}`} className="text-green hover:underline">
              configure roles
            </Link>
            {' '}before marking availability.
          </p>
        </div>
      ) : days.length === 0 ? (
        <div className="bg-white border border-line rounded p-10 text-center">
          <p className="text-sm italic text-ink-soft">
            No operating days — check the event start / end dates.
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-line rounded">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-line flex-wrap">
              <div className="flex-1">
                <div className="text-sm font-semibold">Availability grid</div>
                <div className="text-xs text-ink-soft mt-0.5">
                  Tick the role × day cells you're available for.
                  Click a column or row header to bulk-toggle.
                </div>
              </div>
              <button
                type="button"
                onClick={selectAll}
                className="text-xs font-semibold px-3 py-1 rounded border border-line hover:bg-parchment transition-colors"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={selectNone}
                className="text-xs font-semibold px-3 py-1 rounded border border-line hover:bg-parchment transition-colors"
              >
                Select none
              </button>
              <span className="text-xs text-ink-soft font-mono">
                {tickedCount} ticked
              </span>
            </div>

            <div className="p-4 overflow-x-auto">
              <AvailabilityGrid
                roles={roles}
                days={days}
                picked={picked}
                onToggleCell={toggleCell}
                onToggleColumn={toggleColumn}
                onToggleRow={toggleRow}
              />
            </div>
          </div>

          <div className="bg-white border border-line rounded p-4 space-y-3">
            <label className="block">
              <div className="text-xs uppercase tracking-wide font-semibold text-ink-soft mb-1.5">
                Anything else?
                <span className="ml-2 normal-case text-ink-soft font-normal italic">
                  Optional, max 200 chars — applies to newly-ticked cells on save
                </span>
              </div>
              <input
                type="text"
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                maxLength={200}
                placeholder="e.g. leaving 14:00 on Sunday"
                className="w-full border border-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-green focus:border-green"
              />
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving…' : 'Save availability'}
              </button>
              {!dirty && upsertMut.isSuccess && (
                <span className="text-xs text-green font-semibold">Saved.</span>
              )}
              {dirty && (
                <span className="text-xs text-ink-soft italic">Unsaved changes.</span>
              )}
            </div>
            {serverError && (
              <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-3 text-xs">
                <div className="font-semibold mb-0.5">Could not save</div>
                <pre className="whitespace-pre-wrap font-mono">{serverError.message}</pre>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ---- Grid ---------------------------------------------------------------

function AvailabilityGrid({
  roles, days, picked, onToggleCell, onToggleColumn, onToggleRow
}: {
  roles: LibraryRole[]
  days: string[]
  picked: Set<string>
  onToggleCell: (roleId: number, date: string) => void
  onToggleColumn: (date: string) => void
  onToggleRow: (roleId: number) => void
}) {
  const dayCount = days.length
  return (
    <div
      className="grid gap-1"
      style={{
        gridTemplateColumns: `minmax(200px, 1fr) repeat(${dayCount}, minmax(80px, 1fr))`,
        minWidth: 200 + dayCount * 80
      }}
    >
      {/* Top-left empty corner */}
      <div className="sticky left-0 z-10 px-3 py-2 text-xs uppercase tracking-wide text-ink-soft font-semibold flex items-end bg-white">
        Role / Day
      </div>
      {/* Column headers — clickable */}
      {days.map((d) => {
        const date = parseIsoDate(d)
        return (
          <button
            type="button"
            key={d}
            onClick={() => onToggleColumn(d)}
            className="px-2 py-2 text-center bg-parchment border border-line rounded hover:border-green hover:bg-white transition-colors"
            title="Toggle every role in this day"
          >
            <div className="text-[10px] uppercase tracking-wide text-ink-soft">
              {WEEKDAY_SHORT[date.getUTCDay()]}
            </div>
            <div className="font-mono text-xs font-semibold mt-0.5">
              {date.getUTCDate()} {MONTH_SHORT[date.getUTCMonth()]}
            </div>
          </button>
        )
      })}
      {roles.map((role) => (
        <Fragment key={role.id}>
          {/* Row header — clickable */}
          <button
            type="button"
            onClick={() => onToggleRow(role.id)}
            className="sticky left-0 z-10 px-3 py-2 bg-parchment border border-line rounded hover:border-green hover:bg-white transition-colors text-left"
            title="Toggle every day for this role"
          >
            <div className="text-sm font-semibold flex items-center gap-2">
              <span>{role.name}</span>
              {role.affectsServices && (
                <span
                  title="Affects services"
                  className="inline-block w-1.5 h-1.5 rounded-full bg-brass flex-shrink-0"
                />
              )}
            </div>
            {(role.defaultShiftStart || role.defaultShiftEnd) && (
              <div className="text-[10px] text-ink-soft font-mono mt-0.5">
                {role.defaultShiftStart ?? '?'}–{role.defaultShiftEnd ?? '?'}
              </div>
            )}
          </button>
          {/* Cells */}
          {days.map((d) => {
            const key = `${role.id}|${d}`
            const ticked = picked.has(key)
            return (
              <button
                type="button"
                key={key}
                onClick={() => onToggleCell(role.id, d)}
                aria-pressed={ticked}
                className={
                  'rounded border min-h-[2.5rem] flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-green focus:ring-offset-1 '
                  + (ticked
                    ? 'border-green bg-green text-parchment hover:bg-green/90'
                    : 'border-dashed border-line bg-white text-ink-soft/50 hover:border-green hover:bg-parchment')
                }
              >
                {ticked ? (
                  <span className="text-base font-bold leading-none">✓</span>
                ) : (
                  <span className="text-xs">—</span>
                )}
              </button>
            )
          })}
        </Fragment>
      ))}
    </div>
  )
}
